import api from './api';

export interface MovMoneda {
    codigoMoneda: number;
    fechaCotizacion: string;
    cotizacion: number;
    nombreMoneda?: string | null;
}

export interface MovMonedaSearchParams {
    searchTerm?: string;
    pageNumber?: number;
    pageSize?: number;
}

export interface MovMonedaPagedResult {
    items: MovMoneda[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

export interface CotizacionDia {
    codigoMoneda: number;
    nombreMoneda?: string | null;
    fechaCotizacion: string;
    cotizacion: number;
}

export interface CotizacionDiaRegistro {
    codigoMoneda: number;
    cotizacion: number;
    fechaCotizacion?: string | null;
}

export interface CotizacionDiaRequeridas {
    fecha: string;
    codigosMonedaSinCotizacion: number[];
}

const toFechaApi = (fecha: string | Date): string => {
    if (typeof fecha === 'string') {
        return fecha.includes('T') ? fecha.split('T')[0] : fecha;
    }
    const y = fecha.getFullYear();
    const m = String(fecha.getMonth() + 1).padStart(2, '0');
    const d = String(fecha.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const mapItem = (item: Record<string, unknown>): MovMoneda => {
    const fechaRaw = item.fechaCotizacion ?? item.FechaCotizacion;
    let fechaCotizacion = '';
    if (fechaRaw) {
        const d = new Date(String(fechaRaw));
        fechaCotizacion = Number.isFinite(d.getTime())
            ? d.toISOString().split('T')[0]
            : String(fechaRaw).split('T')[0];
    }
    return {
        codigoMoneda: Number(item.codigoMoneda ?? item.CodigoMoneda ?? 0),
        fechaCotizacion,
        cotizacion: Number(item.cotizacion ?? item.Cotizacion ?? 0),
        nombreMoneda: (item.nombreMoneda ?? item.NombreMoneda) != null
            ? String(item.nombreMoneda ?? item.NombreMoneda).trim()
            : null,
    };
};

const mapCotizacionDia = (item: Record<string, unknown>): CotizacionDia => {
    const mapped = mapItem(item);
    return {
        codigoMoneda: mapped.codigoMoneda,
        nombreMoneda: mapped.nombreMoneda,
        fechaCotizacion: mapped.fechaCotizacion,
        cotizacion: mapped.cotizacion,
    };
};

const getCotizacionValue = (item: MovMoneda): number => {
    const n = Number(item.cotizacion);
    return Number.isFinite(n) && n > 0 ? n : NaN;
};

const getDateMillis = (item: MovMoneda): number => {
    if (!item.fechaCotizacion) return 0;
    const t = new Date(item.fechaCotizacion).getTime();
    return Number.isFinite(t) ? t : 0;
};

const movMonedaService = {
    getMovMonedasPaged: async (params?: MovMonedaSearchParams): Promise<MovMonedaPagedResult> => {
        const resp = await api.get('/api/MovMoneda', {
            params: {
                SearchTerm: params?.searchTerm || undefined,
                PageNumber: params?.pageNumber ?? 1,
                PageSize: params?.pageSize ?? 10,
            },
        });
        const raw = resp?.data;
        if (raw && typeof raw === 'object' && 'items' in raw) {
            const data = raw as {
                items: unknown[];
                totalCount: number;
                pageNumber: number;
                pageSize: number;
                totalPages: number;
            };
            return {
                items: (data.items || []).map((i) => mapItem(i as Record<string, unknown>)),
                totalCount: data.totalCount ?? 0,
                pageNumber: data.pageNumber ?? 1,
                pageSize: data.pageSize ?? 10,
                totalPages: data.totalPages ?? 0,
            };
        }
        const arr = Array.isArray(raw) ? raw : [];
        return {
            items: arr.map((i) => mapItem(i as Record<string, unknown>)),
            totalCount: arr.length,
            pageNumber: 1,
            pageSize: arr.length || 10,
            totalPages: 1,
        };
    },

    getMovMonedas: async (params?: MovMonedaSearchParams): Promise<MovMonedaPagedResult> => {
        return movMonedaService.getMovMonedasPaged(params);
    },

    getAllMovMonedas: async (searchTerm?: string): Promise<MovMoneda[]> => {
        const all: MovMoneda[] = [];
        let page = 1;
        let totalPages = 1;
        do {
            const result = await movMonedaService.getMovMonedasPaged({
                pageNumber: page,
                pageSize: 20,
                searchTerm,
            });
            all.push(...result.items);
            totalPages = result.totalPages || 1;
            page++;
        } while (page <= totalPages);
        return all;
    },

    getMovMoneda: async (codigoMoneda: number, fechaCotizacion: string): Promise<MovMoneda> => {
        const fecha = fechaCotizacion.split('T')[0];
        const response = await api.get(`/api/MovMoneda/${codigoMoneda}/${encodeURIComponent(fecha)}`);
        return mapItem(response.data as Record<string, unknown>);
    },

    getUltimaCotizacion: async (): Promise<number | null> => {
        try {
            const result = await movMonedaService.getMovMonedasPaged({ pageNumber: 1, pageSize: 20 });
            if (!result.items.length) return null;
            const sorted = [...result.items].sort((a, b) => getDateMillis(b) - getDateMillis(a));
            for (const it of sorted) {
                const v = getCotizacionValue(it);
                if (Number.isFinite(v)) return v;
            }
            return null;
        } catch {
            return null;
        }
    },

    getUltimaCotizacionPorMoneda: async (codigoMoneda: number): Promise<number | null> => {
        if (!codigoMoneda || codigoMoneda <= 0) return null;
        try {
            const resp = await api.get('/api/MovMoneda/ultima', { params: { codigoMoneda } });
            const raw = resp?.data;
            if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
            if (raw && typeof raw === 'object') {
                const mapped = mapItem(raw as Record<string, unknown>);
                const v = getCotizacionValue(mapped);
                if (Number.isFinite(v)) return v;
            }
            return null;
        } catch {
            return null;
        }
    },

    createMovMoneda: async (movMoneda: MovMoneda): Promise<MovMoneda> => {
        const response = await api.post('/api/MovMoneda', {
            codigoMoneda: movMoneda.codigoMoneda,
            fechaCotizacion: movMoneda.fechaCotizacion.split('T')[0],
            cotizacion: movMoneda.cotizacion,
        });
        return mapItem(response.data as Record<string, unknown>);
    },

    updateMovMoneda: async (
        codigoMoneda: number,
        fechaCotizacion: string,
        movMoneda: MovMoneda
    ): Promise<void> => {
        const fecha = fechaCotizacion.split('T')[0];
        await api.put(`/api/MovMoneda/${codigoMoneda}/${encodeURIComponent(fecha)}`, {
            codigoMoneda: movMoneda.codigoMoneda,
            fechaCotizacion: movMoneda.fechaCotizacion.split('T')[0],
            cotizacion: movMoneda.cotizacion,
        });
    },

    deleteMovMoneda: async (codigoMoneda: number, fechaCotizacion: string): Promise<void> => {
        const fecha = fechaCotizacion.split('T')[0];
        await api.delete(`/api/MovMoneda/${codigoMoneda}/${encodeURIComponent(fecha)}`);
    },

    getCotizacionDia: async (codigoMoneda: number, fecha: string | Date): Promise<CotizacionDia> => {
        const response = await api.get('/api/MovMoneda/del-dia', {
            params: {
                codigoMoneda,
                fecha: toFechaApi(fecha),
            },
        });
        return mapCotizacionDia(response.data as Record<string, unknown>);
    },

    getCotizacionesRequeridas: async (
        codigosMoneda: number[],
        fecha: string | Date
    ): Promise<CotizacionDiaRequeridas> => {
        const codigos = Array.from(new Set(codigosMoneda.filter((c) => Number.isFinite(c) && c > 0)));
        const response = await api.get('/api/MovMoneda/del-dia/requeridas', {
            params: {
                codigosMoneda: codigos,
                fecha: toFechaApi(fecha),
            },
            paramsSerializer: (params) => {
                const search = new URLSearchParams();
                const fechaParam = String(params.fecha ?? '');
                if (fechaParam) search.append('fecha', fechaParam);
                const list = Array.isArray(params.codigosMoneda) ? params.codigosMoneda : [];
                list.forEach((codigo: number) => search.append('codigosMoneda', String(codigo)));
                return search.toString();
            },
        });
        const raw = (response.data || {}) as Record<string, unknown>;
        const fechaRaw = raw.fecha ?? raw.Fecha;
        const sinRaw = raw.codigosMonedaSinCotizacion ?? raw.CodigosMonedaSinCotizacion;
        const sinList = Array.isArray(sinRaw) ? sinRaw.map((c) => Number(c)).filter((c) => Number.isFinite(c) && c > 0) : [];
        return {
            fecha: fechaRaw ? toFechaApi(String(fechaRaw)) : toFechaApi(fecha),
            codigosMonedaSinCotizacion: sinList,
        };
    },

    registrarCotizacionDia: async (dto: CotizacionDiaRegistro): Promise<CotizacionDia> => {
        const response = await api.post('/api/MovMoneda/del-dia', {
            codigoMoneda: dto.codigoMoneda,
            cotizacion: dto.cotizacion,
            fechaCotizacion: dto.fechaCotizacion ? toFechaApi(dto.fechaCotizacion) : undefined,
        });
        return mapCotizacionDia(response.data as Record<string, unknown>);
    },
};

export default movMonedaService;
